import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "./dialog";
import { Button } from "./button";
import { Input } from "./input";
import { AlertTriangle } from "lucide-react";

export function DeleteConfirmDialog({ 
  open, 
  onOpenChange, 
  onConfirm, 
  title = "Delete Item",
  description = "This action cannot be undone.",
  itemName = ""
}) {
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    if (confirmText.toLowerCase() !== "delete") return;
    setIsDeleting(true);
    try {
      await onConfirm();
    } finally {
      setIsDeleting(false);
      setConfirmText("");
      onOpenChange(false);
    }
  };

  const handleClose = (open) => {
    if (!open) {
      setConfirmText("");
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            {title}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {description}
            {itemName && (
              <span className="block mt-2 font-medium text-slate-900">
                Item: {itemName}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-3">
          <p className="text-sm text-slate-600">
            This decision is <strong>not reversible</strong>. Please type <span className="font-mono bg-red-50 text-red-600 px-1.5 py-0.5 rounded">delete</span> to confirm.
          </p>
          <Input
            data-testid="delete-confirm-input"
            placeholder="Type 'delete' to confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="h-11 rounded-xl"
            autoComplete="off"
          />
        </div>
        
        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            className="flex-1 rounded-xl"
          >
            Cancel
          </Button>
          <Button
            data-testid="delete-confirm-btn"
            onClick={handleConfirm}
            disabled={confirmText.toLowerCase() !== "delete" || isDeleting}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
